import React from 'react';
import { BENTO_ARCHITECTURE_ITEMS } from '../../data/content';
import { DoubleBezelCard } from '../common/DoubleBezelCard';
import { PixelGlyph } from '../common/PixelGlyph';
import { SunsetStripe } from '../common/SunsetStripe';

export const BentoArchitecture: React.FC = () => {
  return (
    <section
      id="architecture"
      className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 max-w-7xl mx-auto z-10"
      aria-label="System Architecture Bento Grid"
    >
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-16 pb-4 sm:pb-6 border-b border-white/10 gap-6">
        <div>
          <div className="flex items-center gap-2.5 text-xs font-mono text-sunset-gold tracking-ultra uppercase mb-3">
            <PixelGlyph name="grid-dots" size={12} color="#ffaf01" />
            <span>SUBSYSTEM DESIGN // GAPLESS BENTO ARCHITECTURE</span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl md:text-5xl text-cream-100">
            Engineered layer by layer.
          </h2>
        </div>
        <p className="max-w-md text-xs sm:text-sm text-cream-muted leading-relaxed font-mono">
          Each card implements our double-bezel concentric enclosure standard with 3D pitch/roll kinematics and hardware-accelerated rendering.
        </p>
      </div>

      {/* Gapless 12-Column Bento Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 grid-flow-dense">
        {BENTO_ARCHITECTURE_ITEMS.map((item) => (
          <div key={item.id} className={`${item.colSpan}`}>
            <DoubleBezelCard
              tilt={true}
              spotlight={true}
              innerClassName="p-5 sm:p-7 md:p-9 flex flex-col justify-between h-full min-h-[240px] sm:min-h-[280px]"
            >
              {/* Card Top */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
                  <span className="text-[10px] sm:text-[11px] font-mono tracking-widest text-sunset-gold uppercase">
                    {item.tag}
                  </span>
                  {item.badge && (
                    <span className="px-2.5 py-0.5 rounded-full bg-sunset-gold/15 text-sunset-gold border border-sunset-gold/30 text-[10px] font-mono font-bold tracking-wider">
                      {item.badge}
                    </span>
                  )}
                </div>

                <h3 className="font-display font-bold text-xl sm:text-2xl text-cream-100 mb-2.5 sm:mb-3">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-cream-muted leading-relaxed mb-5 sm:mb-6">
                  {item.description}
                </p>
              </div>

              {/* Card Bottom: Technical Spec & Metric */}
              <div className="pt-4 sm:pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4">
                <div>
                  <div className="text-[9px] sm:text-[10px] font-mono text-cream-dim tracking-wider uppercase mb-1">
                    ENGINEERING SPEC
                  </div>
                  <div className="text-[11px] sm:text-xs font-mono font-semibold text-cream-200">
                    {item.technicalSpec}
                  </div>
                </div>

                {item.metric && (
                  <div className="text-left sm:text-right">
                    <div className="text-[9px] sm:text-[10px] font-mono text-cream-dim tracking-wider uppercase mb-0.5 sm:mb-1">
                      {item.metric.label}
                    </div>
                    <div className="font-mono font-bold text-xl sm:text-2xl text-sunset-gold">
                      {item.metric.value}
                      <span className="text-xs text-cream-muted ml-0.5">
                        {item.metric.unit}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </DoubleBezelCard>
          </div>
        ))}
      </div>

      {/* Footer stripe */}
      <SunsetStripe className="mt-10 sm:mt-16 opacity-60" height="h-1" />
    </section>
  );
};
